/* ===================================================================
   BIAZZI — theme.js
   Drawer · scroll-aware header · reveal-on-scroll · PDP tabs · detail
   gallery overlay
   =================================================================== */

(function () {
  // Detect Shopify theme editor — skip scroll animations there so re-rendered
  // sections (slider move, image upload) don't blank out at opacity: 0.
  const isDesignMode = !!(window.Shopify && window.Shopify.designMode);

  // ---------- Reveal-on-scroll ----------
  function revealAll(root) {
    (root || document).querySelectorAll('.fade-in:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  }
  function setupReveal(root) {
    if (isDesignMode) { revealAll(root); return; }
    const els = (root || document).querySelectorAll('.fade-in:not(.is-visible)');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
  }

  // ---------- Hero parallax (logo morphs from center → header on scroll) ----------
  let heroParallaxState = null;
  function setupHeroParallax() {
    // Tear down previous instance if section reloaded
    if (heroParallaxState && heroParallaxState.cleanup) heroParallaxState.cleanup();

    const section = document.querySelector('[data-hero-parallax]');
    const logo = section ? section.querySelector('[data-hero-logo]') : null;
    const headerLogo = document.querySelector('.header__logo');

    if (!section || !logo || !headerLogo) {
      document.body.classList.remove('has-hero-parallax', 'is-parallax-done');
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.body.classList.add('has-hero-parallax');

    if (reducedMotion) {
      // Skip morph entirely; show header logo, hide hero logo
      logo.setAttribute('data-faded', 'true');
      document.body.classList.add('is-parallax-done');
      return;
    }

    let rafId = null;
    let lastProgress = -1;
    const heroBottom = section.querySelector('[data-hero-bottom]');

    // Cache values that don't change during scroll. Recompute only on resize.
    let cache = { sectionHeight: 0, viewportH: 0, viewportW: 0, logoH: 0,
                  endX: 0, endY: 0, endScale: 0, startX: 0, startY: 0, totalScroll: 0 };

    function recompute() {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const sectionH = section.offsetHeight;
      const logoH = logo.offsetHeight || vh * 0.18;
      const tRect = headerLogo.getBoundingClientRect();
      const headerLogoH = tRect.height || 26;
      const bottomMargin = Math.max(24, vh * 0.06);
      // Header bottom edge — where the opaque creme strip ends in viewport.
      // We use the header element's bottom rect since the user could have
      // resized or the header height varies between mobile/desktop.
      const headerEl = document.querySelector('.header');
      const headerBottomEdge = headerEl ? headerEl.getBoundingClientRect().bottom : 80;
      cache = {
        sectionHeight: sectionH,
        viewportH: vh,
        viewportW: vw,
        logoH: logoH,
        startX: vw / 2,
        startY: vh - (logoH / 2) - bottomMargin,
        endX: tRect.left + tRect.width / 2,
        endY: tRect.top + tRect.height / 2,
        endScale: headerLogoH / logoH,
        totalScroll: sectionH - vh,
        headerBottomEdge: headerBottomEdge,
      };
      lastProgress = -1; // force re-apply on next tick
    }

    function tick() {
      rafId = null;
      // Single layout read per frame — only getBoundingClientRect.top of the section
      const sectionTop = section.getBoundingClientRect().top;
      const progress = cache.totalScroll > 0
        ? Math.min(1, Math.max(0, -sectionTop / cache.totalScroll))
        : 0;

      if (Math.abs(progress - lastProgress) < 0.001) return;
      lastProgress = progress;

      // Cubic ease-out
      const eased = 1 - Math.pow(1 - progress, 3);
      const x = cache.startX + (cache.endX - cache.startX) * eased;
      const y = cache.startY + (cache.endY - cache.startY) * eased;
      const scale = 1 + (cache.endScale - 1) * eased;

      logo.style.setProperty('--hero-logo-x', x + 'px');
      logo.style.setProperty('--hero-logo-y', y + 'px');
      logo.style.setProperty('--hero-logo-scale', scale.toFixed(4));

      // Color cross-fade based on POSITION (not progress) — logo turns preto
      // when its top edge approaches the header bottom. Fully preto by the
      // time it overlaps the opaque creme header strip.
      const logoVisualHeight = cache.logoH * scale;
      const logoTopY = y - logoVisualHeight / 2;
      const fadeStartY = cache.headerBottomEdge + 140; // start fading 140px below header
      const fadeEndY = cache.headerBottomEdge - 20;    // fully black 20px past header bottom (well overlapping)
      let blackness = (fadeStartY - logoTopY) / (fadeStartY - fadeEndY);
      blackness = Math.min(1, Math.max(0, blackness));
      logo.style.setProperty('--hero-logo-creme-opacity', (1 - blackness).toFixed(3));
      logo.style.setProperty('--hero-logo-preto-opacity', blackness.toFixed(3));

      if (heroBottom) {
        const captionOpacity = Math.min(1, Math.max(0, (progress - 0.25) / 0.35));
        heroBottom.style.setProperty('--hero-bottom-opacity', captionOpacity.toFixed(3));
      }

      // Handoff: fade hero logo out, header logo in
      if (progress >= 0.985) {
        if (!document.body.classList.contains('is-parallax-done')) {
          document.body.classList.add('is-parallax-done');
          logo.setAttribute('data-faded', 'true');
        }
      } else if (document.body.classList.contains('is-parallax-done')) {
        document.body.classList.remove('is-parallax-done');
        logo.removeAttribute('data-faded');
      }
    }

    function onScroll() {
      if (rafId) return;
      rafId = requestAnimationFrame(tick);
    }

    // Debounced resize handler — recompute cache then tick once
    let resizeId = null;
    function onResize() {
      clearTimeout(resizeId);
      resizeId = setTimeout(() => { recompute(); tick(); }, 80);
    }

    recompute();
    tick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    // iOS Safari fires orientationchange separately
    window.addEventListener('orientationchange', onResize, { passive: true });

    heroParallaxState = {
      cleanup: () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
        if (rafId) cancelAnimationFrame(rafId);
        if (resizeId) clearTimeout(resizeId);
        document.body.classList.remove('has-hero-parallax', 'is-parallax-done');
        logo.removeAttribute('data-faded');
      },
    };
  }

  // ---------- Shop the Look (hotspots + synced product slider) ----------
  function setupShopLook(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const sections = scope.querySelectorAll('[data-shop-look]');
    const isEditor = !!(window.Shopify && window.Shopify.designMode);
    if (isEditor) document.body.classList.add('is-shopify-editor');

    sections.forEach((section) => {
      if (section.__shopLookInit) return;
      section.__shopLookInit = true;

      const rows = Array.from(section.querySelectorAll('[data-look-row]'));
      const prevBtn = section.querySelector('[data-look-prev]');
      const nextBtn = section.querySelector('[data-look-next]');
      const lookDots = Array.from(section.querySelectorAll('[data-look-dot]'));
      let activeLook = 0;

      function setActiveLook(idx) {
        if (idx < 0) idx = rows.length - 1;
        if (idx >= rows.length) idx = 0;
        activeLook = idx;
        rows.forEach((r, i) => r.classList.toggle('is-active', i === idx));
        lookDots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
      prevBtn && prevBtn.addEventListener('click', () => setActiveLook(activeLook - 1));
      nextBtn && nextBtn.addEventListener('click', () => setActiveLook(activeLook + 1));
      lookDots.forEach((d) => d.addEventListener('click', () => {
        setActiveLook(parseInt(d.getAttribute('data-look-index'), 10) || 0);
      }));

      // Auto-rotate Looks every 8s; pause permanently on the first user interaction.
      const AUTO_ROTATE_MS = 8000;
      let autoRotateId = null;
      let userInteracted = false;
      function startAutoRotate() {
        if (rows.length <= 1 || userInteracted || isEditor) return;
        if (autoRotateId) clearInterval(autoRotateId);
        autoRotateId = setInterval(() => setActiveLook(activeLook + 1), AUTO_ROTATE_MS);
      }
      function pauseAutoRotate() {
        userInteracted = true;
        if (autoRotateId) { clearInterval(autoRotateId); autoRotateId = null; }
      }
      // Wire up pause to any look-level click
      [prevBtn, nextBtn, ...lookDots].forEach((el) => {
        if (el) el.addEventListener('click', pauseAutoRotate, { once: false });
      });

      // Per-row: hotspot ↔ card ↔ dot sync + product nav arrows + counter
      rows.forEach((row) => {
        const hotspots = Array.from(row.querySelectorAll('[data-look-hotspot]'));
        const cards = Array.from(row.querySelectorAll('[data-product-card]'));
        const dots = Array.from(row.querySelectorAll('[data-product-dot]'));
        const productPrev = row.querySelector('[data-product-prev]');
        const productNext = row.querySelector('[data-product-next]');
        const counter = row.querySelector('[data-product-counter]');
        const productsEl = row.querySelector('[data-look-products]');
        const productTrack = row.querySelector('[data-product-track]');
        const productCount = cards.length;
        let activeProduct = 0;

        function setActiveProduct(idx) {
          if (idx < 0) idx = productCount - 1;
          if (idx >= productCount) idx = 0;
          activeProduct = idx;
          hotspots.forEach((h) => h.classList.toggle('is-active', parseInt(h.getAttribute('data-product-index'), 10) === idx));
          cards.forEach((c) => c.classList.toggle('is-active', parseInt(c.getAttribute('data-product-index'), 10) === idx));
          dots.forEach((d) => d.classList.toggle('is-active', parseInt(d.getAttribute('data-product-index'), 10) === idx));
          if (counter && productCount > 1) counter.textContent = `${idx + 1} / ${productCount}`;
        }

        // On mobile the products sit BELOW the image — when a hotspot is tapped,
        // scroll down so the matching product is in view (under the sticky header).
        function isMobileLook() {
          return !window.matchMedia('(min-width: 1024px)').matches;
        }
        function scrollProductsIntoView() {
          if (!productsEl || !isMobileLook()) return;
          const header = document.querySelector('.header');
          const headerH = header ? header.getBoundingClientRect().height : 0;
          const top = productsEl.getBoundingClientRect().top + window.pageYOffset - headerH - 12;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }

        hotspots.forEach((h) => h.addEventListener('click', (e) => {
          pauseAutoRotate();
          if (h.__justDragged) { h.__justDragged = false; e.preventDefault(); return; }
          setActiveProduct(parseInt(h.getAttribute('data-product-index'), 10));
          scrollProductsIntoView();
        }));
        dots.forEach((d) => d.addEventListener('click', () => {
          pauseAutoRotate();
          setActiveProduct(parseInt(d.getAttribute('data-product-index'), 10));
        }));
        productPrev && productPrev.addEventListener('click', () => { pauseAutoRotate(); setActiveProduct(activeProduct - 1); });
        productNext && productNext.addEventListener('click', () => { pauseAutoRotate(); setActiveProduct(activeProduct + 1); });

        // ----- Swipe the product image left/right to change product (mobile) -----
        if (productTrack && productCount > 1) {
          let swipeStartX = 0, swipeStartY = 0, swiping = false;
          productTrack.addEventListener('touchstart', (e) => {
            if (e.touches.length !== 1) return;
            swipeStartX = e.touches[0].clientX;
            swipeStartY = e.touches[0].clientY;
            swiping = true;
          }, { passive: true });
          productTrack.addEventListener('touchend', (e) => {
            if (!swiping) return;
            swiping = false;
            const t = e.changedTouches[0];
            const dx = t.clientX - swipeStartX;
            const dy = t.clientY - swipeStartY;
            // Horizontal intent only — ignore mostly-vertical gestures (page scroll).
            if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
            pauseAutoRotate();
            setActiveProduct(dx < 0 ? activeProduct + 1 : activeProduct - 1);
          }, { passive: true });
        }

        // ----- Swipe the LOOK image left/right to change look (mobile, fora do editor) -----
        if (!isEditor && rows.length > 1) {
          const visualEl = row.querySelector('.shop-look__visual');
          if (visualEl) {
            let vx = 0, vy = 0, vSwiping = false;
            visualEl.addEventListener('touchstart', (e) => {
              if (e.touches.length !== 1) return;
              vx = e.touches[0].clientX; vy = e.touches[0].clientY; vSwiping = true;
            }, { passive: true });
            visualEl.addEventListener('touchend', (e) => {
              if (!vSwiping) return;
              vSwiping = false;
              const t = e.changedTouches[0];
              const dx = t.clientX - vx;
              const dy = t.clientY - vy;
              if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
              pauseAutoRotate();
              setActiveLook(dx < 0 ? activeLook + 1 : activeLook - 1);
            }, { passive: true });
          }
        }

        // ----- Drag-to-position helpers (theme editor only) -----
        if (isEditor) {
          const visual = row.querySelector('.shop-look__visual');
          if (visual) visual.classList.add('is-editor-drag');
          hotspots.forEach((h) => attachHotspotDrag(h));
          // Drag the IMAGE itself to reposition its focal point
          attachImageDrag(row);
        }
      });

      setActiveLook(0);
      startAutoRotate();
    });
  }

  function attachImageDrag(row) {
    const visual = row.querySelector('.shop-look__visual');
    if (!visual) return;
    // Pick the visible <img> (mobile or desktop, whichever's currently displayed)
    function activeImg() {
      const mq = window.matchMedia('(min-width: 768px)').matches;
      const sel = mq ? '.shop-look__media--desktop img' : '.shop-look__media--mobile img';
      return visual.querySelector(sel) || visual.querySelector('img');
    }
    function readObj(img) {
      const m = (img.style.objectPosition || '').match(/(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
      return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 50, y: 50 };
    }
    let dragging = false, startX, startY, startObj, visRect, img;
    visual.addEventListener('pointerdown', (e) => {
      // Skip if hotspot was clicked (its handler will run instead)
      if (e.target.closest('[data-look-hotspot]')) return;
      img = activeImg();
      if (!img) return;
      visRect = visual.getBoundingClientRect();
      startX = e.clientX; startY = e.clientY;
      startObj = readObj(img);
      dragging = true;
      try { visual.setPointerCapture(e.pointerId); } catch (_) {}
      e.preventDefault();
    });
    visual.addEventListener('pointermove', (e) => {
      if (!dragging || !img) return;
      // Drag right → reveal MORE of the LEFT side → object-position X decreases
      const dx = ((e.clientX - startX) / visRect.width) * 100;
      const dy = ((e.clientY - startY) / visRect.height) * 100;
      const x = Math.max(0, Math.min(100, startObj.x - dx));
      const y = Math.max(0, Math.min(100, startObj.y - dy));
      img.style.objectPosition = `${Math.round(x)}% ${Math.round(y)}%`;
      visual.setAttribute('data-image-drag-pos', `Imagem · X: ${Math.round(x)}  ·  Y: ${Math.round(y)}`);
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      setTimeout(() => visual.removeAttribute('data-image-drag-pos'), 4500);
    }
    visual.addEventListener('pointerup', endDrag);
    visual.addEventListener('pointercancel', endDrag);
  }

  function attachHotspotDrag(hotspot) {
    let dragging = false;
    let visualRect = null;
    let moved = false;

    function onDown(e) {
      const visual = hotspot.closest('.shop-look__visual');
      if (!visual) return;
      visualRect = visual.getBoundingClientRect();
      dragging = true;
      moved = false;
      hotspot.setPointerCapture(e.pointerId);
      // Stop the click from firing setActiveProduct
      e.stopPropagation();
    }
    function onMove(e) {
      if (!dragging || !visualRect) return;
      moved = true;
      const x = Math.max(0, Math.min(100, ((e.clientX - visualRect.left) / visualRect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - visualRect.top) / visualRect.height) * 100));
      hotspot.style.setProperty('--hs-x', x + '%');
      hotspot.style.setProperty('--hs-y', y + '%');
      hotspot.setAttribute('data-drag-pos', `X: ${Math.round(x)}  ·  Y: ${Math.round(y)}`);
    }
    function onUp() {
      if (!dragging) return;
      dragging = false;
      if (moved) {
        hotspot.__justDragged = true;
        // Keep tooltip visible briefly so Rod can read & copy values
        setTimeout(() => { hotspot.removeAttribute('data-drag-pos'); }, 4500);
      }
    }
    hotspot.addEventListener('pointerdown', onDown);
    hotspot.addEventListener('pointermove', onMove);
    hotspot.addEventListener('pointerup', onUp);
    hotspot.addEventListener('pointercancel', onUp);
  }

  // ---------- Header scroll-aware ----------
  function setupHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const variant = header.getAttribute('data-variant');
    if (variant !== 'transparent') return;
    function update() {
      header.setAttribute('data-scrolled', window.scrollY > 60 ? 'true' : 'false');
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // ---------- SHOP Mega-menu (desktop dropdown / mobile fullscreen) ----------
  function setupShopMenu() {
    const menu = document.querySelector('[data-shop-menu]');
    const toggles = document.querySelectorAll('[data-shop-toggle]');
    const closeBtn = document.querySelector('[data-shop-close]');
    const overlay = document.querySelector('[data-shop-overlay]');
    if (!menu || !toggles.length) return;
    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      document.body.style.overflow = 'hidden';
    }
    function shut() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    }
    toggles.forEach((t) => t.addEventListener('click', (e) => {
      e.preventDefault();
      menu.classList.contains('is-open') ? shut() : open();
    }));
    closeBtn && closeBtn.addEventListener('click', shut);
    overlay && overlay.addEventListener('click', shut);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) shut();
    });
  }

  // ---------- Mobile menu (hamburger) ----------
  function setupMobileMenu() {
    const menu = document.querySelector('[data-mobile-menu]');
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const closeBtn = document.querySelector('[data-mobile-menu-close]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    if (!menu || !toggle) return;
    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function shut() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    toggle.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', shut);
    overlay && overlay.addEventListener('click', shut);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) shut();
    });
  }

  // ---------- PDP tabs ----------
  function setupTabs() {
    const tabs = document.querySelectorAll('[data-tab]');
    const panels = document.querySelectorAll('[data-panel]');
    if (!tabs.length) return;
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        tabs.forEach((t) => t.dataset.active = t.dataset.tab === target ? 'true' : 'false');
        panels.forEach((p) => p.hidden = p.dataset.panel !== target);
      });
    });
  }

  // ---------- PDP detail overlay ----------
  function setupDetailOverlay() {
    const overlay = document.querySelector('[data-detail-overlay]');
    const open = document.querySelector('[data-detail-open]');
    const close = document.querySelector('[data-detail-close]');
    const scroller = document.querySelector('[data-detail-scroll]');
    const counter = document.querySelector('[data-detail-counter]');
    if (!overlay || !open || !close || !scroller) return;
    function show() {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      scroller.scrollLeft = 0;
      updateCounter();
    }
    function hide() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    function updateCounter() {
      if (!counter) return;
      const slides = scroller.children.length;
      const idx = Math.round(scroller.scrollLeft / scroller.clientWidth) + 1;
      counter.textContent = Math.min(slides, Math.max(1, idx)) + ' / ' + slides;
    }
    open.addEventListener('click', show);
    close.addEventListener('click', hide);
    scroller.addEventListener('scroll', updateCounter, { passive: true });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) hide();
    });
  }

  // ---------- Cart drawer (sacola lateral) ----------
  function setupCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const overlay = drawer.querySelector('[data-cart-overlay]');
    const itemsEl = drawer.querySelector('[data-cart-items]');
    const emptyEl = drawer.querySelector('[data-cart-empty]');
    const footerEl = drawer.querySelector('[data-cart-footer]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    let busy = false;

    function money(cents, currency) {
      try {
        return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: currency || 'BRL' });
      } catch (e) {
        return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
      }
    }
    function updateCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = n <= 0;
      });
    }
    function render(cart) {
      updateCount(cart.item_count);
      if (!cart.items || cart.items.length === 0) {
        itemsEl.innerHTML = '';
        emptyEl.hidden = false;
        footerEl.hidden = true;
        return;
      }
      emptyEl.hidden = true;
      footerEl.hidden = false;
      subtotalEl.textContent = money(cart.total_price, cart.currency);
      const installmentsEl = drawer.querySelector('[data-cart-installments]');
      if (installmentsEl) {
        installmentsEl.textContent = '6x de ' + money(Math.round(cart.total_price / 6), cart.currency);
      }
      itemsEl.innerHTML = cart.items.map((it) => {
        const variant = (it.variant_title && it.variant_title !== 'Default Title') ? '<p class="cart-item__variant">' + it.variant_title + '</p>' : '';
        const img = it.image ? '<img class="cart-item__img" src="' + it.image + '" alt="" loading="lazy">' : '<span class="cart-item__img"></span>';
        return '<div class="cart-item" data-key="' + it.key + '">' +
          img +
          '<div class="cart-item__main">' +
            '<div class="cart-item__top"><span class="cart-item__name">' + it.product_title + '</span>' +
            '<span class="cart-item__price">' + money(it.final_line_price, cart.currency) + '</span></div>' +
            variant +
            '<div class="cart-item__row">' +
              '<span class="cart-item__qty">' +
                '<button type="button" data-qty-dec aria-label="Diminuir">−</button>' +
                '<span>' + it.quantity + '</span>' +
                '<button type="button" data-qty-inc aria-label="Aumentar">+</button>' +
              '</span>' +
              '<button type="button" class="cart-item__remove" data-qty-remove>Remover</button>' +
            '</div>' +
          '</div></div>';
      }).join('');
    }
    function fetchCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
    }
    function change(key, quantity) {
      if (busy) return;
      busy = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
        .then((cart) => { render(cart); })
        .catch(() => { fetchCart().then(render).catch(() => {}); })
        .then(() => { busy = false; });
    }
    // Recomendações ("Você pode gostar") baseadas no 1º item do carrinho
    const recsWrap = drawer.querySelector('[data-cart-recs]');
    const recsGrid = drawer.querySelector('[data-cart-recs-grid]');
    function loadRecs(cart) {
      if (!recsWrap || !recsGrid || !cart.items || !cart.items.length) {
        if (recsWrap) recsWrap.hidden = true;
        return;
      }
      const pid = cart.items[0].product_id;
      fetch('/recommendations/products.json?product_id=' + pid + '&limit=4&intent=related', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((data) => {
          const recs = (data.products || []).filter((p) => !cart.items.some((it) => it.product_id === p.id)).slice(0, 4);
          if (!recs.length) { recsWrap.hidden = true; return; }
          recsGrid.innerHTML = recs.map((p) => {
            const img = (p.featured_image || (p.images && p.images[0]) || '');
            return '<div class="cart-rec">' +
              '<a href="' + p.url + '"><img class="cart-rec__img" src="' + img + '" alt="' + (p.title || '') + '" loading="lazy"></a>' +
              '<p class="cart-rec__name">' + p.title + '</p>' +
              '<span class="cart-rec__price">' + money(p.price, cart.currency) + '</span>' +
              '<p class="installments installments--compact">6x de ' + money(Math.round(p.price / 6), cart.currency) + ' sem juros</p>' +
              '<button type="button" class="cart-rec__qv" data-qv-open data-handle="' + p.handle + '">Visualização rápida</button>' +
              '</div>';
          }).join('');
          recsWrap.hidden = false;
        })
        .catch(() => { recsWrap.hidden = true; });
    }

    function open() {
      fetchCart().then((cart) => {
        render(cart);
        loadRecs(cart);
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('.quickview-overlay.is-open')) document.body.style.overflow = '';
    }
    window.openCartDrawer = open;

    // ----- Quick view -----
    const qvOverlay = document.querySelector('[data-quickview-overlay]');
    const qvContent = qvOverlay && qvOverlay.querySelector('[data-quickview-content]');
    function qvClose() {
      if (!qvOverlay) return;
      qvOverlay.classList.remove('is-open');
      qvOverlay.setAttribute('aria-hidden', 'true');
      if (!drawer.classList.contains('is-open')) document.body.style.overflow = '';
    }
    function openQuickView(handle) {
      if (!qvOverlay || !qvContent) return;
      fetch('/products/' + handle + '?section_id=quick-view', { headers: { 'Accept': 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          qvContent.innerHTML = html;
          qvOverlay.classList.add('is-open');
          qvOverlay.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          wireQuickView();
        })
        .catch(() => {});
    }
    function wireQuickView() {
      const qv = qvContent.querySelector('[data-quickview]');
      if (!qv) return;
      const atc = qv.querySelector('[data-qv-atc]');
      let selected = atc ? atc.getAttribute('data-default-variant') : null;
      const sizes = qv.querySelectorAll('[data-qv-size]');
      // marca o tamanho do default como ativo
      sizes.forEach((b) => { if (b.getAttribute('data-variant') === selected) b.classList.add('is-active'); });
      sizes.forEach((b) => {
        b.addEventListener('click', () => {
          if (b.classList.contains('is-unavailable')) return;
          sizes.forEach((x) => x.classList.remove('is-active'));
          b.classList.add('is-active');
          selected = b.getAttribute('data-variant');
        });
      });
      atc && atc.addEventListener('click', () => {
        if (!selected || atc.disabled) return;
        atc.disabled = true;
        const orig = atc.textContent;
        atc.textContent = 'Adicionando…';
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: selected, quantity: 1 })
        })
          .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
          .then(() => { qvClose(); open(); })
          .catch(() => { atc.disabled = false; atc.textContent = orig; alert('Não foi possível adicionar.'); });
      });
    }
    if (qvOverlay) {
      qvOverlay.querySelector('[data-quickview-bg]').addEventListener('click', qvClose);
      qvOverlay.querySelector('[data-quickview-close]').addEventListener('click', qvClose);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && qvOverlay.classList.contains('is-open')) qvClose(); });
    }
    // delega clique no botão "Visualização rápida" das recs
    recsGrid && recsGrid.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qv-open]');
      if (b) { e.preventDefault(); openQuickView(b.getAttribute('data-handle')); }
    });

    // Abrir pelo ícone da sacola
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); open(); });
    });
    overlay && overlay.addEventListener('click', close);
    drawer.querySelectorAll('[data-cart-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });

    // Quantidade / remover (event delegation)
    itemsEl.addEventListener('click', (e) => {
      const row = e.target.closest('.cart-item');
      if (!row) return;
      const key = row.getAttribute('data-key');
      const qtyEl = row.querySelector('.cart-item__qty span');
      const current = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
      if (e.target.closest('[data-qty-inc]')) change(key, current + 1);
      else if (e.target.closest('[data-qty-dec]')) change(key, Math.max(0, current - 1));
      else if (e.target.closest('[data-qty-remove]')) change(key, 0);
    });
  }

  // ---------- Init ----------
  function init() {
    setupReveal();
    setupHeader();
    setupHeroParallax();
    setupShopMenu();
    setupMobileMenu();
    setupShopLook();
    setupTabs();
    setupDetailOverlay();
    setupCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor — re-init when a section is loaded/re-rendered
  // (triggered when you move a slider, upload an image, or change any setting).
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    revealAll(section);
    setupReveal(section);
    // Re-init parallax if the hero section was the one reloaded
    if (section.querySelector && section.querySelector('[data-hero-parallax]')) {
      setupHeroParallax();
    }
    // Re-init shop/mobile menu if header reloaded
    if (section.querySelector && (section.querySelector('[data-shop-menu]') || section.querySelector('[data-mobile-menu]'))) {
      setupShopMenu();
      setupMobileMenu();
    }
    // Re-init Shop the Look when its section reloads
    if (section.querySelector && section.querySelector('[data-shop-look]')) {
      setupShopLook(section);
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    revealAll(e.target);
  });
  document.addEventListener('shopify:block:select', (e) => {
    revealAll(e.target);
  });
})();
